import {
  Box,
  DialogContent,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  Select,
  TextField,
  Typography,
  MenuItem,
  Collapse,
  Alert,
  FormControlLabel,
  Stack,
} from '@mui/material';
import CustomButton from '../../../../components/customButton/CustomButton';
import CloseButton from '../../../../components/closebutton/CloseButton';
import { ClientControllerAction, ClientControllerState } from '../../controller/client-controller';
import { RoleInfoModel } from '../../../role/model/role.model';
import { PositionModel } from '../../../position/model/position.model';
import { MinistryModel, ProvinceModel } from '../../../documents/models/destination.model';
import { DepartmentModel } from '../../../department/model/department.model';
import Checkbox from '@mui/material/Checkbox';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { clientCreateSchema } from '../../model/client.schema';
import { ClientInfoCreateModel } from '../../model/clitent-model';
import { useEffect } from 'react';

const DRAFT_KEY = 'client_create_draft';

const loadDraft = (): Partial<ClientInfoCreateModel> => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const saveDraft = (values: Partial<ClientInfoCreateModel>) => {
  localStorage.setItem(DRAFT_KEY, JSON.stringify(values));
};

const clearDraft = () => localStorage.removeItem(DRAFT_KEY);

const ClientModalCreate = ({ ctrl }: { ctrl: ClientControllerState & ClientControllerAction }) => {
  const draft = loadDraft();

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<ClientInfoCreateModel>({
    resolver: yupResolver(clientCreateSchema),
    defaultValues: {
      status: true,
      show_accept_button: false,
      ...draft,
    },
  });

  useEffect(() => {
    const subscription = watch((values) => saveDraft(values));
    return () => subscription.unsubscribe();
  }, [watch]);

  const onSubmit = (data: ClientInfoCreateModel) => {
    clearDraft();
    ctrl.onSubmitForm(data);
  };

  const onCancel = () => {
    clearDraft();
    ctrl.onHandleModal(null);
  };

  const topError = errors.ministry_id?.message || (errors as any)['']?.message;

  return (
    <DialogContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">ເພີ່ມຜູ້ໃຊ້ງານແອັບໃໝ່</Typography>
        <CloseButton onClick={onCancel} />
      </Box>

      <Divider sx={{ mb: '10px', mt: 2 }} />
      <Collapse unmountOnExit orientation="vertical" in={!!(ctrl?.message || topError)}>
        <Alert severity="error" sx={{ mb: 1 }}>{ctrl?.message || topError}</Alert>
      </Collapse>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container marginTop="10px" spacing={2}>

          {/* ── ຂໍ້ມູນສ່ວນຕົວ ── */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              ຂໍ້ມູນສ່ວນຕົວ
            </Typography>
            <Divider />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              label="ຊື່ແທ້"
              placeholder="ທອງດີ"
              error={!!errors.first_name}
              helperText={errors.first_name?.message}
              {...register('first_name')}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              label="ນາມສະກຸນ"
              placeholder="ມົງຄົນສັກ"
              error={!!errors.last_name}
              helperText={errors.last_name?.message}
              {...register('last_name')}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              type="number"
              InputLabelProps={{ shrink: true }}
              label="ເບີໂທ"
              placeholder="20xx xxx xxx"
              error={!!errors.phone}
              helperText={errors.phone?.message}
              {...register('phone')}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              label="ອີເມວ"
              placeholder="abc@example.com"
              error={!!errors.email}
              helperText={errors.email?.message}
              {...register('email')}
            />
          </Grid>

          {/* ── ບົດບາດ & ຕຳແໜ່ງ ── */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, mt: 1 }}>
              ບົດບາດ & ຕຳແໜ່ງ
            </Typography>
            <Divider />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="role_id"
              control={control}
              render={({ field }) => (
                <FormControl size="small" fullWidth error={!!errors.role_id}>
                  <InputLabel shrink>ບົດບາດຜູ້ໃຊ້</InputLabel>
                  <Select
                    label="ບົດບາດຜູ້ໃຊ້"
                    value={field.value ?? ''}
                    displayEmpty
                    renderValue={() => {
                      const found = ctrl.listRoles?.find(
                        (e: RoleInfoModel) => String(e.id) === field.value
                      );
                      return (
                        <span style={{ color: '#5A5A5A' }}>
                          {found ? found.role_name : 'ເລືອກບົດບາດ'}
                        </span>
                      );
                    }}
                  >
                    {ctrl.listRoles?.map((item: RoleInfoModel, index: number) => (
                      <MenuItem
                        key={index}
                        value={String(item.id)}
                        onClick={() => field.onChange(String(item.id))}
                      >
                        <Typography marginLeft={2}>{item.role_name}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.role_id && (
                    <Typography variant="caption" color="error">
                      {errors.role_id.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="position_id"
              control={control}
              render={({ field }) => (
                <FormControl size="small" fullWidth error={!!errors.position_id}>
                  <InputLabel shrink>ຕຳແໜ່ງ</InputLabel>
                  <Select
                    label="ຕຳແໜ່ງ"
                    value={field.value ?? ''}
                    displayEmpty
                    renderValue={() => {
                      const found = ctrl.listPosition?.find((e) => e.uuid === field.value);
                      return (
                        <span style={{ color: '#5A5A5A' }}>
                          {found ? found.position_name_la : 'ເລືອກຕຳແໜ່ງ'}
                        </span>
                      );
                    }}
                  >
                    <MenuItem value="" onClick={() => field.onChange('')}>
                      <Typography marginLeft={2} sx={{ color: 'grey' }}>ບໍ່ເລືອກ</Typography>
                    </MenuItem>
                    {ctrl.listPosition?.map((item: PositionModel, index: number) => (
                      <MenuItem
                        key={index}
                        value={item.uuid}
                        onClick={() => field.onChange(item.uuid)}
                      >
                        <Typography marginLeft={2}>{item.position_name_la}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.position_id && (
                    <Typography variant="caption" color="error">
                      {errors.position_id.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </Grid>

          {/* ── ສະຖານທີ ── */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, mt: 1 }}>
              ສະຖານທີ
            </Typography>
            <Divider />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="ministry_id"
              control={control}
              render={({ field }) => (
                <FormControl size="small" fullWidth>
                  <InputLabel shrink>ກະຊວງ</InputLabel>
                  <Select
                    label="ກະຊວງ"
                    value={field.value ?? ''}
                    displayEmpty
                    renderValue={() => {
                      const found = ctrl.listMinistry?.find((e) => e.uuid === field.value);
                      return (
                        <span style={{ color: '#5A5A5A' }}>
                          {found ? found.title_la : 'ເລືອກກະຊວງ'}
                        </span>
                      );
                    }}
                  >
                    <MenuItem value="" onClick={() => field.onChange('')}>
                      <Typography marginLeft={2} sx={{ color: 'grey' }}>ບໍ່ເລືອກ</Typography>
                    </MenuItem>
                    {ctrl.listMinistry?.map((item: MinistryModel, index: number) => (
                      <MenuItem
                        key={index}
                        value={item.uuid}
                        onClick={() => field.onChange(item.uuid)}
                      >
                        <Typography marginLeft={2}>{item.title_la}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="province_id"
              control={control}
              render={({ field }) => (
                <FormControl size="small" fullWidth>
                  <InputLabel shrink>ແຂວງ</InputLabel>
                  <Select
                    label="ແຂວງ"
                    value={field.value ?? ''}
                    displayEmpty
                    renderValue={() => {
                      const found = ctrl.listProvince?.find((e) => e.uuid === field.value);
                      return (
                        <span style={{ color: '#5A5A5A' }}>
                          {found ? found.title_la : 'ເລືອກແຂວງ'}
                        </span>
                      );
                    }}
                  >
                    <MenuItem value="" onClick={() => field.onChange('')}>
                      <Typography marginLeft={2} sx={{ color: 'grey' }}>ບໍ່ເລືອກ</Typography>
                    </MenuItem>
                    {ctrl.listProvince?.map((item: ProvinceModel, index: number) => (
                      <MenuItem
                        key={index}
                        value={item.uuid}
                        onClick={() => field.onChange(item.uuid)}
                      >
                        <Typography marginLeft={2}>{item.title_la}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="department_id"
              control={control}
              render={({ field }) => (
                <FormControl size="small" fullWidth>
                  <InputLabel shrink>ກົມ</InputLabel>
                  <Select
                    label="ກົມ"
                    value={field.value ?? ''}
                    displayEmpty
                    renderValue={() => {
                      const found = ctrl.listDepartment?.find((e) => e.uuid === field.value);
                      return (
                        <span style={{ color: '#5A5A5A' }}>
                          {found ? found.title_la : 'ເລືອກກົມ'}
                        </span>
                      );
                    }}
                  >
                    <MenuItem value="" onClick={() => field.onChange('')}>
                      <Typography marginLeft={2} sx={{ color: 'grey' }}>ບໍ່ເລືອກ</Typography>
                    </MenuItem>
                    {ctrl.listDepartment?.map((item: DepartmentModel, index: number) => (
                      <MenuItem
                        key={index}
                        value={item.uuid}
                        onClick={() => field.onChange(item.uuid)}
                      >
                        <Typography marginLeft={2}>{item.title_la}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          {/* ── ເນື້ອໃນ & ສະຖານະ ── */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, mt: 1 }}>
              ເພີ່ມເຕີມ
            </Typography>
            <Divider />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              multiline
              rows={3}
              InputLabelProps={{ shrink: true }}
              label="ຄໍາອະທິບາຍ"
              placeholder="ປ້ອນຄໍາອະທິບາຍ"
              {...register('description')}
            />
          </Grid>

          <Grid item xs={6}>
            <Stack spacing={0}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value ?? true}
                        onChange={(_, v) => field.onChange(v)}
                      />
                    }
                    label="ສະຖານະຜູ້ໃຊ້ (ເປີດໃຊ້ງານ)"
                  />
                )}
              />
              <Controller
                name="show_accept_button"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value ?? false}
                        onChange={(_, v) => field.onChange(v)}
                      />
                    }
                    label="ເປີດໃຫ້ຮັບເອກະສານໄດ້"
                  />
                )}
              />
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3, mt: 4 }} />

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <CustomButton
              backgroundColor="#EEEFF3"
              color="black"
              label="ຍົກເລີກ"
              handleOpenDialog={onCancel}
            />
          </Grid>
          <Grid item xs={6}>
            <CustomButton label="ເພີ່ມຜູ້ໃຊ້ງານແອັບ" handleOpenDialog={handleSubmit(onSubmit)} />
          </Grid>
        </Grid>
      </form>
    </DialogContent>
  );
};

export default ClientModalCreate;
